import type { MessageKey } from "./keys";

/**
 * English copy. Must stay key-for-key identical to `ar.ts` — the `Record<MessageKey, string>`
 * annotation makes a missing key a type error rather than a runtime fallback.
 *
 * ADR-003: copy must never promise a refund timeline we do not control. The shopper's
 * resolution choice is a request.
 */
export const en: Record<MessageKey, string> = {
  "eligibility.window_closed": "The return window for this item has closed.",
  "eligibility.sku_excluded": "This item is not returnable.",
  "eligibility.already_returned":
    "A return has already been requested for this item.",
  "eligibility.not_delivered": "This order has not been delivered yet.",
  "eligibility.eligible": "This item can be returned.",

  "error.order_not_found": "We could not find an order with those details.",
  "error.customer_blocked":
    "Returns are not available for this account. Please contact the store.",
  "error.returns_disabled": "This store is not accepting returns right now.",
  "error.policy_violation":
    "This request does not meet the store’s return policy.",
  "error.rate_limited": "Too many attempts. Please try again in a few minutes.",
  "error.internal": "Something went wrong on our side. Please try again.",
  "error.invalid_code":
    "That code is wrong or has expired. Try again or ask for a new one.",
  "error.cancel_not_allowed":
    "This return can no longer be cancelled. It may already be on its way to the warehouse.",

  "violation.returns_disabled":
    "This store is not accepting returns right now.",
  "violation.order_not_eligible": "This order is not eligible for a return.",
  "violation.window_closed": "The return window for this item has closed.",
  "violation.sku_excluded": "This item is not returnable.",
  "violation.customer_blocked": "Returns are not available for this account.",
  "violation.reason_not_offered":
    "This store does not accept that return reason.",
  "violation.photo_required":
    "A photo is required for the reason you selected.",
  "violation.note_required":
    "Tell the store what happened when you choose Something else.",
  "violation.resolution_not_offered":
    "That resolution is not offered by this store.",
  "violation.no_resolution_available":
    "This store has no return resolutions enabled.",
  "violation.dropoff_not_allowed":
    "Drop-off is not available. Pickup is the only option.",
  "violation.qty_exceeds_ordered": "You cannot return more than you ordered.",
  "violation.line_not_found": "One of the selected items is not on this order.",
  "violation.no_lines_selected": "Select at least one item to return.",
  "violation.cancel_not_allowed": "This return can no longer be cancelled.",

  "reason.size": "Wrong size or fit",
  "reason.damaged": "Arrived damaged",
  "reason.notlike": "Not as described",
  "reason.changed": "Changed my mind",
  "reason.wrong": "Wrong item sent",
  "reason.spoiled": "Arrived spoiled or past its date",
  "reason.other": "Something else",

  // System-originated. The shopper chose none of these; they are stated as events, not as
  // the shopper's words.
  "reason.cod_refused":
    "Payment on delivery was refused, so the parcel came back",
  "reason.delivery_failed":
    "Delivery could not be completed, so the parcel came back",

  "resolution.refund": "Request a refund",
  "resolution.exchange": "Request an exchange",
  "resolution.credit": "Request store credit",

  "collection.pickup": "Pickup from my address",
  "collection.dropoff": "Drop off at a location",

  "state.requested": "Requested",
  "state.approved": "In review",
  "state.awaiting_pickup": "In review",
  "state.in_transit": "On the way back",
  "state.received": "Received at the warehouse",
  "state.inspected": "Inspected",
  "state.booking_failed": "Collection could not be booked yet",
  "state.cancelled": "Cancelled by you",
  "state.restocked": "Returned to stock",
  "state.quarantined": "Held for review",
  "state.disposed": "Disposed",
  "state.rejected": "Rejected",
  "state.lost_in_transit": "Lost in transit",
  "state.expired": "Expired",
  "state.completed": "Completed",

  "state_group.waiting_for_you": "Waiting for you",
  "state_group.waiting_for_pickup": "Waiting for pickup",
  "state_group.with_courier": "With the courier",
  "state_group.at_warehouse": "At the warehouse",
  "state_group.inspected": "Inspected",
  "state_group.refund_due": "Refund due",
  "state_group.closed": "Closed",
  "state_group.rejected": "Rejected",
  "state_group.cancelled": "Cancelled",
  "actor.customer": "You",
  "actor.merchant": "The store",
  "actor.warehouse": "The warehouse",
  "actor.system": "Automatically",
  "actor.carrier": "The courier",

  "event.created": "Return requested",
  "event.unknown": "Update",

  "grade.sellable": "As new",
  "grade.damaged": "Damaged",
  "grade.missing": "Missing",

  "rejection.merchant_declined": "The store declined this return.",
  "rejection.policy_refused":
    "This return does not meet the store’s return policy.",
  "rejection.item_not_as_declared":
    "The item received did not match what was declared.",
  "rejection.outside_window":
    "The item was sent back after the return window closed.",

  "fee.none": "No return fee",
  "fee.flat": "A return fee applies to this request.",
  "fee.percent": "A return fee applies as a percentage of the item value.",

  "portal.closed": "This store is not accepting returns right now.",
  "portal.no_resolutions_enabled":
    "This store has not enabled any return options.",
  "portal.pickup_only": "Pickup is the only collection option for this store.",
  "portal.photo_required_hint":
    "Add a photo so the store can review your request.",
  "portal.resolution_is_a_request":
    "Your choice is sent to the store as a request. The store decides and processes it.",

  "screen.lookup.title": "Find your order",
  "screen.lookup.phone_description":
    "Enter the mobile number you ordered with. We will send you a code and show you your orders.",
  "screen.lookup.code_title": "Enter the code",
  "screen.lookup.code_description":
    "We sent a six digit code to your mobile. It works for ten minutes.",
  "screen.lookup.orders_title": "Choose the order",
  "screen.lookup.orders_description":
    "These are the orders on your number with this store. Pick the one you want to return from.",
  "screen.lookup.description":
    "Enter your order number and the mobile number on the order. No account needed.",
  "screen.items.title": "Choose the items",
  "screen.items.description":
    "Select what you want to return and how many of each.",
  "screen.reason.title": "Reason for return",
  "screen.reason.description": "Tell the store why each item is coming back.",
  "screen.evidence.title": "Photos and notes",
  "screen.evidence.description":
    "A photo helps the store review your request faster.",
  "screen.resolution.title": "What you would like",
  "screen.resolution.description": "Choose what you are asking the store for.",
  "screen.review.title": "Review your request",
  "screen.review.description":
    "Check the details, then choose how the items are collected.",
  "screen.confirmation.title": "Your return request is in",
  "screen.track.title": "Track your return",
  "screen.track.description":
    "Enter your return reference and the mobile number on the order.",

  "action.continue": "Continue",
  "action.back": "Back",
  "action.submit": "Submit request",
  "action.retry": "Try again",
  "action.track": "Track this return",
  "action.find_order": "Find my order",
  "action.send_code": "Send me a code",
  "action.verify_code": "Confirm",
  "action.resend_code": "Send a new code",
  "action.change_phone": "Change the number",
  "action.have_order_ref": "I have my order number",
  "action.use_phone_instead": "Sign in with my mobile instead",
  "action.skip": "Skip this step",
  "action.add_photo": "Add a photo",
  "action.remove_photo": "Remove photo",
  "action.start_over": "Start over",
  "action.change": "Change",
  "action.cancel_return": "Cancel this return",
  "action.cancel_confirm": "Yes, cancel this return",
  "action.keep_return": "Keep my return",
  "action.refresh_status": "Refresh status",

  "ui.loading": "Loading",
  "ui.offline": "No connection. Your details are safe. Reconnect and carry on.",
  "ui.error_generic": "Something went wrong. Please try again.",
  "ui.empty_generic": "There is nothing to show here.",
  "ui.step_of": "Step",
  "ui.step_label.lookup": "Find",
  "ui.step_label.items": "Items",
  "ui.step_label.reason": "Reason",
  "ui.step_label.evidence": "Photos",
  "ui.step_label.resolution": "Resolve",
  "ui.step_label.review": "Review",
  "ui.language_switch": "Switch language",
  "ui.optional": "Optional",
  "ui.route_not_found":
    "This page does not exist. Check the link the store sent you.",
  "ui.server_confirms":
    "The store confirms the final details when it reviews your request.",

  "field.order_ref.label": "Order number",
  "field.order_ref.hint":
    "The number on your order confirmation from the store, for example 267115052.",
  "field.order_ref.placeholder": "267115052",
  "field.phone.label": "Mobile number",
  "field.phone.hint": "The number used on the order.",
  "field.phone.placeholder": "05XXXXXXXX",
  "field.code.label": "Verification code",
  "field.code.hint": "Six digits, from the message we just sent.",
  "validation.code_required": "Enter the six digit code.",
  "shopper.orders.empty":
    "We found no orders on this number with this store. If you have your order number, enter it instead.",
  "shopper.orders.items": "items",
  "shopper.orders.placed": "Placed",
  "field.reference.label": "Return reference",
  "field.reference.placeholder": "RTN-00000",
  "field.sub_reason.label": "More detail",
  "field.note.label": "Note for the store",
  "field.note.hint": "Anything the store should know. Optional.",
  "field.other_note.label": "What happened?",
  "field.other_note.hint":
    "A sentence is enough. The store reads it before deciding.",
  "field.quantity.label": "Quantity to return",

  "validation.order_ref_required": "Enter your order number.",
  "validation.phone_required": "Enter the mobile number on the order.",
  "validation.reference_required": "Enter your return reference.",
  "validation.reason_required": "Choose a reason for every item.",
  "validation.resolution_required": "Choose what you would like.",
  "validation.collection_required": "Choose how the items are collected.",
  "validation.photo_required": "Add a photo for every item that needs one.",
  "validation.other_note_required":
    "Tell the store what happened with this item.",
  "validation.select_at_least_one": "Select at least one item.",

  "items.select_hint":
    "Items that cannot be returned are shown with the reason.",
  "items.none_eligible":
    "None of the items on this order can be returned right now.",
  "items.empty": "This order has no items to show.",

  "reason.select_hint": "These are the reasons this store accepts.",
  "reason.none_offered": "This store has not enabled any return reasons.",

  "evidence.required_note":
    "A photo is required for the reason you chose. You cannot continue without one.",
  "evidence.optional_note":
    "A photo is not required here. You can add one if it helps.",
  "evidence.photos_label": "Photos",
  "evidence.uploading": "Uploading photo",
  "evidence.upload_failed": "The photo could not be uploaded. Try again.",
  "evidence.no_photos": "No photos added.",

  "resolution.select_hint": "Only the options this store offers are shown.",
  "resolution.exchange_unavailable":
    "Exchange is unavailable because a replacement is out of stock.",

  "fee.disclosure_title": "Return fee",
  "fee.deducted_note":
    "The store deducts this fee from the value of the returned items.",
  "fee.total_label": "Total return fee",

  "review.items_heading": "Items",
  "review.reason_heading": "Reason",
  "review.resolution_heading": "What you asked for",
  "review.collection_heading": "Collection",
  "review.collection_hint": "Choose how the items leave you.",
  "review.evidence_heading": "Photos and notes",
  "review.photos_attached": "Photos attached",
  "review.fee_heading": "Fee",

  "confirmation.heading": "The store has your request",
  "confirmation.reference_label": "Your return reference",
  "confirmation.what_next":
    "The store reviews your request and you will be told when the items are collected. Keep this reference to track it.",

  "track.intro": "Track a return you already requested.",
  "track.reference_label": "Return reference",
  "track.copy_reference": "Copy",
  "track.copy_link": "Copy link",
  "track.copied": "Copied",
  "track.keep_hint":
    "Keep this to check your return later. You'll need your mobile number too.",
  "track.lines_heading": "Items",
  "track.timeline_heading": "Progress",
  "track.received_note":
    "Your items are back with Sirdab and logged against this return.",
  "track.rejection_heading": "Why this item was rejected",
  "track.approved_headline": "Your return has been approved",
  "track.grade_label": "Condition on inspection",
  "track.qty_expected": "Declared",
  "track.qty_received": "Received",
  "track.empty_timeline": "Nothing has happened on this return yet.",

  "cancel.confirm_title": "Confirm cancelling this return",
  "cancel.confirm_body":
    "Cancelling cannot be undone. If you change your mind later you will need to start a new return, and the return window may have closed by then.",
  "cancel.scope_note": "Cancelling covers every item in this return.",
  "cancel.done": "This return has been cancelled. Nothing will be collected.",
  "cancel.unavailable_note":
    "This return can no longer be cancelled here. Some items are already on their way to the warehouse.",
  "cancel.failed_note":
    "We could not send your cancellation. Your return has not been cancelled, and you can try again.",
};
